import { css } from "styled-system/css";

// Editorial kit for the admin pages (settings, tools, labels): forms and
// tables in ink on paper. Square inputs with hairline borders, ink primary
// buttons, outlined secondary buttons, status as a glyph plus text, mono for
// IDs, counts, timestamps and keys, dense ruled tables.
//
// Pairs with the admin components in ./admin-kit.tsx. Settings adds a few
// page-specific keys on top in features/settings/admin-styles.ts.

export const ak = {
  page: css({
    display: "flex",
    flexDirection: "column",
    maxWidth: "980px",
    pb: "56px",
    fontFamily: "sans",
    color: "ed.ink",
  }),

  // ── Page head ──────────────────────────────────────────────────────────
  back: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    alignSelf: "flex-start",
    fontFamily: "sans",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "ed.ink3",
    textDecoration: "none",
    _hover: { color: "ed.ink" },
  }),
  kicker: css({
    fontFamily: "sans",
    fontWeight: 700,
    fontSize: "12px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "ed.ink2",
    mt: "18px",
    mb: "6px",
  }),
  headRow: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "12px 24px",
    pb: "14px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
  }),
  headline: css({
    fontFamily: "serif",
    fontWeight: 700,
    fontSize: { base: "28px", md: "36px" },
    lineHeight: 1.1,
    letterSpacing: "-0.015em",
    m: 0,
    textWrap: "balance",
  }),
  dek: css({
    fontFamily: "serif",
    fontSize: "16.5px",
    lineHeight: 1.45,
    color: "ed.ink2",
    m: 0,
    mt: "8px",
    maxWidth: "680px",
  }),
  headActions: css({
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "center",
  }),

  // ── Sections ───────────────────────────────────────────────────────────
  sect: css({
    borderTopWidth: "3px",
    borderTopStyle: "solid",
    borderColor: "ed.ink",
    pt: "8px",
    mt: "36px",
  }),
  sectHead: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "8px 16px",
    mb: "12px",
  }),
  sectKicker: css({
    fontFamily: "sans",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "ed.ink3",
  }),
  h2: css({
    fontFamily: "sans",
    fontWeight: 800,
    fontSize: "20px",
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
    m: 0,
    mt: "4px",
  }),
  sectDek: css({
    fontFamily: "sans",
    fontSize: "13px",
    lineHeight: 1.5,
    color: "ed.ink2",
    m: 0,
    mt: "4px",
    maxWidth: "640px",
  }),
  /** One-line method note under a table or list. */
  note: css({
    fontFamily: "sans",
    fontSize: "12px",
    lineHeight: 1.5,
    color: "ed.ink3",
    m: 0,
    mt: "10px",
    maxWidth: "720px",
    "& strong": { color: "ed.ink2", fontWeight: 700 },
  }),

  // ── Type helpers ───────────────────────────────────────────────────────
  mono: css({ fontFamily: "mono", fontVariantNumeric: "tabular-nums" }),
  muted: css({ color: "ed.ink3" }),
  link: css({
    color: "ed.ink",
    textDecoration: "underline",
    textDecorationColor: "ed.rule",
    textUnderlineOffset: "2px",
    _hover: { textDecorationColor: "ed.ink" },
  }),

  // ── Controls ───────────────────────────────────────────────────────────
  field: css({
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    mb: "16px",
    minWidth: 0,
  }),
  label: css({
    fontFamily: "sans",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink2",
  }),
  hint: css({
    fontFamily: "sans",
    fontSize: "12px",
    lineHeight: 1.45,
    color: "ed.ink3",
    m: 0,
  }),
  input: css({
    width: "100%",
    px: "10px",
    py: "7px",
    fontSize: "14px",
    color: "ed.ink",
    bg: "ed.paper",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: "0",
    outline: "none",
    transition: "border-color 300ms cubic-bezier(0.2, 0, 0, 1)",
    _hover: { borderColor: "ed.ink3" },
    _focusVisible: {
      borderColor: "ed.ink",
      outline: "1px solid",
      outlineColor: "ed.ink",
      outlineOffset: "0",
    },
    _placeholder: { color: "ed.ink3" },
    _disabled: { opacity: 0.5, cursor: "not-allowed" },
  }),
  select: css({
    width: "100%",
    px: "10px",
    py: "7px",
    pr: "28px",
    fontSize: "14px",
    color: "ed.ink",
    bg: "ed.paper",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: "0",
    outline: "none",
    cursor: "pointer",
    appearance: "none",
    // Chevron drawn as two hairlines in the ink3 gray.
    backgroundImage:
      "linear-gradient(45deg, transparent 50%, var(--colors-ed-ink3) 50%), linear-gradient(135deg, var(--colors-ed-ink3) 50%, transparent 50%)",
    backgroundPosition: "calc(100% - 15px) 50%, calc(100% - 10px) 50%",
    backgroundSize: "5px 5px, 5px 5px",
    backgroundRepeat: "no-repeat",
    _hover: { borderColor: "ed.ink3" },
    _focusVisible: {
      borderColor: "ed.ink",
      outline: "1px solid",
      outlineColor: "ed.ink",
      outlineOffset: "0",
    },
    _disabled: { opacity: 0.5, cursor: "not-allowed" },
  }),
  /**
   * Compact select for table cells and filter bars, on top of `select`.
   * Sets only what never collides with `select`: two atomic classes for the
   * same property resolve by stylesheet order, not by class order.
   */
  // Modifiers end values in "!" so they win whatever order Panda emits
  // the classes in (as btnSm and iconBtnDanger do)
  selectSm: css({
    width: "auto!",
    pl: "8px!",
  }),
  textarea: css({
    width: "100%",
    px: "10px",
    py: "8px",
    fontSize: "14px",
    lineHeight: 1.5,
    color: "ed.ink",
    bg: "ed.paper",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: "0",
    outline: "none",
    resize: "vertical",
    _hover: { borderColor: "ed.ink3" },
    _focusVisible: {
      borderColor: "ed.ink",
      outline: "1px solid",
      outlineColor: "ed.ink",
      outlineOffset: "0",
    },
    _placeholder: { color: "ed.ink3" },
  }),
  checkRow: css({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "14px",
    fontWeight: 600,
    color: "ed.ink",
    cursor: "pointer",
    "& input": {
      width: "16px",
      height: "16px",
      m: 0,
      accentColor: "var(--colors-ed-ink)",
      cursor: "pointer",
    },
  }),
  formActions: css({
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "center",
    pt: "14px",
    mt: "4px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderColor: "ed.rule",
  }),

  btn: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    px: "14px",
    py: "8px",
    fontFamily: "sans",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    color: "ed.paper",
    bg: "ed.ink",
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: "0",
    cursor: "pointer",
    textDecoration: "none",
    transition: "background 300ms cubic-bezier(0.2, 0, 0, 1)",
    _hover: { bg: "ed.ink2", borderColor: "ed.ink2" },
    _disabled: { opacity: 0.4, cursor: "not-allowed" },
  }),
  btnSecondary: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    px: "14px",
    py: "8px",
    fontFamily: "sans",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    color: "ed.ink",
    bg: "transparent",
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: "0",
    cursor: "pointer",
    textDecoration: "none",
    transition: "background 300ms cubic-bezier(0.2, 0, 0, 1)",
    _hover: { bg: "ed.panel" },
    _disabled: { opacity: 0.4, cursor: "not-allowed" },
  }),
  /** Destructive: outlined in a hairline, turns caution on hover. */
  btnDanger: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    px: "14px",
    py: "8px",
    fontFamily: "sans",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    color: "ed.ink2",
    bg: "transparent",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: "0",
    cursor: "pointer",
    _hover: { color: "ed.caution", borderColor: "ed.caution" },
    _disabled: { opacity: 0.4, cursor: "not-allowed" },
  }),
  /**
   * Small size on top of `btn`, `btnSecondary` or `btnDanger`. `!` so it wins
   * regardless of stylesheet order; height stays the base button's.
   */
  btnSm: css({ px: "9px!", fontSize: "12px!", gap: "4px!" }),
  /** Text button: underlined sans, no box. */
  btnText: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    p: 0,
    bg: "transparent",
    border: "none",
    fontFamily: "sans",
    fontSize: "12.5px",
    color: "ed.ink2",
    textDecoration: "underline",
    textDecorationColor: "ed.rule",
    textUnderlineOffset: "2px",
    cursor: "pointer",
    _hover: { color: "ed.ink", textDecorationColor: "ed.ink" },
    _disabled: { opacity: 0.4, cursor: "not-allowed" },
  }),
  iconBtn: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    p: 0,
    color: "ed.ink2",
    bg: "transparent",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: "0",
    cursor: "pointer",
    flexShrink: 0,
    _hover: { color: "ed.ink", borderColor: "ed.ink" },
    _disabled: { opacity: 0.4, cursor: "not-allowed" },
  }),
  /** On top of `iconBtn`; `!` so the caution hover beats its ink hover. */
  iconBtnDanger: css({
    _hover: { color: "ed.caution!", borderColor: "ed.caution!" },
  }),
  actions: css({
    display: "flex",
    gap: "6px",
    flexShrink: 0,
    alignItems: "center",
  }),

  /** Square on/off switch in ink. */
  switchWrap: css({
    position: "relative",
    display: "inline-flex",
    flexShrink: 0,
    cursor: "pointer",
    "& input": {
      position: "absolute",
      opacity: 0,
      width: 0,
      height: 0,
    },
  }),
  switch: css({
    width: "34px",
    height: "18px",
    border: "1px solid",
    borderColor: "ed.ink3",
    bg: "ed.paper",
    position: "relative",
    transition: "background 300ms cubic-bezier(0.2, 0, 0, 1)",
    "&::after": {
      content: '""',
      position: "absolute",
      top: "2px",
      left: "2px",
      width: "12px",
      height: "12px",
      bg: "ed.ink3",
      transition: "transform 300ms cubic-bezier(0.2, 0, 0, 1)",
    },
    '&[data-checked="true"]': { bg: "ed.ink", borderColor: "ed.ink" },
    '&[data-checked="true"]::after': {
      bg: "ed.paper",
      transform: "translateX(16px)",
    },
    "input:focus-visible + &": {
      outline: "2px solid",
      outlineColor: "ed.ink",
      outlineOffset: "2px",
    },
  }),

  /** Uppercase tabs with an ink underline on the active one. */
  tabs: css({
    display: "flex",
    flexWrap: "wrap",
    gap: "18px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
  }),
  tab: css({
    py: "8px",
    mb: "-1px",
    bg: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    fontFamily: "sans",
    fontSize: "11.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink3",
    cursor: "pointer",
    whiteSpace: "nowrap",
    _hover: { color: "ed.ink" },
    '&[aria-selected="true"]': { color: "ed.ink", borderColor: "ed.ink" },
    "& .num": {
      fontFamily: "mono",
      fontVariantNumeric: "tabular-nums",
      letterSpacing: 0,
      ml: "4px",
    },
  }),

  // ── Tables ─────────────────────────────────────────────────────────────
  tableWrap: css({ width: "100%", overflowX: "auto" }),
  table: css({
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: "sans",
    fontSize: "13px",
    color: "ed.ink",
    "& th": {
      fontSize: "10.5px",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "ed.ink2",
      textAlign: "left",
      verticalAlign: "bottom",
      px: "8px",
      py: "6px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.ink",
      whiteSpace: "nowrap",
    },
    "& td": {
      px: "8px",
      py: "7px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.grid",
      verticalAlign: "middle",
    },
    // Numeric cells: <td data-num>. Mono, right-aligned.
    "& [data-num]": {
      textAlign: "right",
      fontFamily: "mono",
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
    "& th:first-child, & td:first-child": { pl: 0 },
    "& th:last-child, & td:last-child": { pr: 0 },
    "& tbody tr:hover td": { bg: "ed.panel" },
  }),

  // ── Ruled lists (connection rows) ──────────────────────────────────────
  list: css({
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderColor: "ed.ink",
  }),
  avatar: css({
    width: "28px",
    height: "28px",
    flexShrink: 0,
    border: "1px solid",
    borderColor: "ed.rule",
    objectFit: "cover",
    filter: "grayscale(1)",
  }),

  // ── Status and banners ─────────────────────────────────────────────────
  status: css({
    display: "inline-flex",
    alignItems: "baseline",
    gap: "6px",
    fontFamily: "sans",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "ed.ink",
    whiteSpace: "nowrap",
    "& [data-glyph]": {
      fontSize: "10px",
      lineHeight: 1,
      width: "10px",
      textAlign: "center",
    },
    '&[data-kind="fail"] [data-glyph]': { color: "ed.caution" },
    '&[data-kind="off"], &[data-kind="wait"]': { color: "ed.ink2" },
  }),
  banner: css({
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    mt: "16px",
    px: "12px",
    py: "10px",
    fontFamily: "sans",
    fontSize: "13.5px",
    lineHeight: 1.45,
    color: "ed.ink",
    bg: "ed.panel",
    borderLeft: "none",
    borderTopWidth: "2px",
    borderTopStyle: "solid",
    borderColor: "ed.ink",
    '&[data-tone="caution"]': {
      bg: "ed.cautionTint",
      borderColor: "ed.caution",
    },
    "& [data-glyph]": { fontSize: "11px", pt: "3px" },
    '&[data-tone="caution"] [data-glyph]': { color: "ed.caution" },
  }),
  bannerDismiss: css({
    ml: "auto",
    p: 0,
    bg: "transparent",
    border: "none",
    color: "ed.ink2",
    cursor: "pointer",
    _hover: { color: "ed.ink" },
  }),
  empty: css({
    py: "28px",
    fontFamily: "serif",
    fontSize: "16px",
    fontStyle: "italic",
    color: "ed.ink2",
    "& p": { m: 0 },
    "& p + p": {
      mt: "4px",
      fontFamily: "sans",
      fontStyle: "normal",
      fontSize: "13px",
      color: "ed.ink3",
    },
  }),
  loading: css({
    fontFamily: "mono",
    fontSize: "12px",
    color: "ed.ink3",
    py: "32px",
  }),

  // ── Definition list (detail pages) ─────────────────────────────────────
  dl: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", sm: "180px minmax(0, 1fr)" },
    m: 0,
    fontSize: "14px",
    lineHeight: 1.5,
    "& dt": {
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "ed.ink3",
      pt: { base: "10px", sm: "9px" },
      pb: { base: "0", sm: "8px" },
      borderBottom: { base: "none", sm: "1px solid" },
      borderColor: "ed.grid",
    },
    "& dd": {
      m: 0,
      py: "7px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.grid",
      minWidth: 0,
      overflowWrap: "anywhere",
    },
  }),

  // ── Modal ──────────────────────────────────────────────────────────────
  overlay: css({
    position: "fixed",
    inset: 0,
    bg: "rgba(18, 18, 18, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    p: "16px",
  }),
  // Opaque paper, never bg.surface.
  modal: css({
    width: "100%",
    maxWidth: "460px",
    maxHeight: "90vh",
    overflowY: "auto",
    bg: "ed.paper",
    color: "ed.ink",
    border: "1px solid",
    borderColor: "ed.ink",
    borderTopWidth: "3px",
    borderTopStyle: "solid",
    borderTopColor: "ed.ink",
    borderRadius: "0",
    p: "20px 22px",
  }),
  modalHead: css({
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "12px",
    mb: "16px",
  }),
  modalTitle: css({
    fontFamily: "serif",
    fontSize: "22px",
    fontWeight: 700,
    lineHeight: 1.2,
    m: 0,
  }),
  modalClose: css({
    p: 0,
    bg: "transparent",
    border: "none",
    color: "ed.ink3",
    cursor: "pointer",
    _hover: { color: "ed.ink" },
  }),
  modalActions: css({
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    pt: "14px",
    mt: "4px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderColor: "ed.rule",
  }),

  // ── Tags ───────────────────────────────────────────────────────────────
  tagRow: css({ display: "flex", flexWrap: "wrap", gap: "4px" }),
  tag: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    px: "6px",
    py: "1px",
    fontFamily: "sans",
    fontSize: "11.5px",
    fontWeight: 600,
    lineHeight: 1.5,
    color: "ed.ink",
    border: "1px solid",
    borderColor: "ed.ink3",
    borderRadius: "0",
    whiteSpace: "nowrap",
  }),
  swatch: css({
    display: "inline-block",
    width: "8px",
    height: "8px",
    flexShrink: 0,
  }),
};
